import { cloneElement, type ReactNode } from 'react';

import { ContentModuleStyle } from '@silvertours/common-landingpages-view';

import { WaveSets } from './WaveSets';
import type { WaveVariants } from './WaveSets/types';

import {
  FixedWave,
  FixedWaveContent,
  FixedWaveWaves,
  FixedWaveEnd,
  ScalableWave,
  ScalableWaveEnd,
} from './Wave.styles';

type WaveProps = {
  type: keyof typeof WaveSets;
  children: ReactNode;
  fullWidth?: boolean;
  backgroundStyle?: ContentModuleStyle;
  extendBottom?: boolean;
};

/** `WaveSvg` hides the variant that does not match the current viewport. */
const bothSizes = (wave: WaveVariants) => (
  <>
    {cloneElement(wave.small, { waveSize: 'small' })}
    {cloneElement(wave.large, { waveSize: 'large' })}
  </>
);

const Wave = ({
  type,
  children,
  fullWidth,
  backgroundStyle,
  extendBottom,
}: WaveProps) => {
  const { start, end, height } = WaveSets[type];
  const startWave = start && bothSizes(start);
  const endWave = end && bothSizes(end);

  // Fixed waves have a fixed height and are positioned underneath the content
  // (basically a background that does not get cut off).
  const isFixedWave = type.startsWith('fixed');
  if (isFixedWave) {
    return (
      <FixedWave>
        <FixedWaveWaves
          isFullWidth={fullWidth}
          heights={height}
          backgroundStyle={backgroundStyle}
          aria-hidden="true"
        >
          {startWave}
          {endWave && <FixedWaveEnd>{endWave}</FixedWaveEnd>}
        </FixedWaveWaves>
        <FixedWaveContent>{children}</FixedWaveContent>
      </FixedWave>
    );
  }

  return (
    <ScalableWave
      waveType={type}
      isFullWidth={fullWidth}
      backgroundStyle={backgroundStyle}
      extendBottom={extendBottom}
    >
      {startWave}
      {children}
      {endWave && (
        <ScalableWaveEnd
          waveType={type}
          isFullWidth={fullWidth}
          extendBottom={extendBottom}
        >
          {endWave}
        </ScalableWaveEnd>
      )}
    </ScalableWave>
  );
};

export { Wave };
